import * as ngCore from '@angular/core'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import { TypeAsserter } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import * as dms from '../../../dms'
import * as organizations from '../../organizations'
import * as gql from '../gql'
import type { TypeParams } from '../types'

/** Standard users API, serving their assignments to organizations. */
@ngCore.Injectable()
export class Api implements ec.models.Api<TypeParams> {
  /** @see {@link dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(dms.schema.Feature)

  /** @see {@link organizations.Feature} */
  private readonly _organizationsFeature = ngCore.inject(organizations.Feature)

  /** @inheritdoc */
  public list = ec.models.Api.list.using.nothingButContext<TypeParams>(
    context =>
      context ? gql.queries.standardUsers.executor(context) : rxjs.of([]),
  )

  /** @inheritdoc */
  public create: ec.models.Api<TypeParams>['create'] = (
    context,
    { organizationIds = [], userId },
  ) => {
    if (!context) {
      return rxjs.of(undefined)
    }

    return gql.mutations.assignStandardUser.executor({
      ...context,
      organizationIds,
      userId,
    })
  }

  /** @inheritdoc */
  public delete: ec.models.Api<TypeParams>['delete'] = (context, id) => {
    TypeAsserter.assertIsNotNullish(context)

    return gql.mutations.removeAccessAssignment.executor({ ...context, id })
  }

  /**
   * Every organization entity in the schema (e.g. Division, Department) gets a
   * column of its own, presenting the organization the user is assigned to at
   * that level.
   *
   * @inheritdoc
   */
  public getAttributes: ec.models.Api<TypeParams>['getAttributes'] = () =>
    this._schemaFeature.selectors.schema.asObservable
      .pipe(
        // NOTE: The schema starts as an empty placeholder, so wait for the
        // loaded one to avoid emitting columns without organization entities
        rxjs.filter(schema => schema.areModelsInitialized),
        rxjs.map(schema =>
          schema.getEntitiesInDimension(organizations.constants.DIMENSION_ID),
        ),
      )
      .pipe(
        rxjs.switchMap(entities =>
          entities.length > 0
            ? rxjs.combineLatest(
                entities.map(entity =>
                  this._organizationsFeature.selectors
                    .getOrganizationEc(entity.id)
                    .asObservable.pipe(
                      rxjs.switchMap(
                        orgEc =>
                          orgEc?.plugins.cachePlugin.feature.selectors.lookup
                            .asObservable ?? rxjs.of(undefined),
                      ),
                      rxjs.map(
                        lookup =>
                          ({
                            getValueFrom: ({ organization }) => {
                              const organizationId = organization?.[entity.id]
                              return organizationId
                                ? lookup?.[organizationId]?.name
                                : undefined
                            },
                            id: entity.id,
                            name: entity.name,
                          }) satisfies ec.models.Attribute<TypeParams>,
                      ),
                    ),
                ),
              )
            : rxjs.of([]),
        ),
        rxjs.map(organizationAttrs => [
          ec.models.Attribute.fromKey<TypeParams>('userId', {
            name: 'User Id',
          }),
          ec.models.Attribute.fromKey<TypeParams>('name', { name: 'Name' }),
          ...organizationAttrs,
        ]),
      )
}
